import React, { useState, useEffect } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

import PrincipalPage from './pages/listings/PrincipalPage';
import ProgramPage from './pages/listings/ProgramPage';
import TenantPage from './pages/listings/TenantPage';
import VpaPage from './pages/listings/VpaPage';
import TenantDetailPage from './pages/detail/TenantDetailPage';
import TenantEditPage from './pages/detail/TenantEditPage';
import LoginPage from './pages/auth/LoginPage';
import QrScanner from './pages/scan/QrScanPage';
import AppLayout from './components/layout/AppLayout';
import HomeScreen from './features/home/HomeScreen';
import CollectScreen from './features/collect/CollectScreen';
import ShopsScreen from './features/shops/ShopsScreen';
import ShopDetailScreen from './features/shops/ShopDetailScreen';

interface JwtPayload {
  exp: number;
}

const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  const decoded = jwtDecode<JwtPayload>(token);
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('accessToken');
  return !!token && !isTokenExpired(token);
};

interface PrivateRouteProps {
  children: React.ReactElement;
}

function PrivateRoute({ children }: PrivateRouteProps) {
  const token = localStorage.getItem('accessToken');

  if (isTokenExpired(token)) {
    localStorage.removeItem('accessToken');
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppContent() {
  const [auth, setAuth] = useState<boolean>(isAuthenticated());
  const location = useLocation();

  useEffect(() => {
    const checkAuth = () => {
      const isValid = isAuthenticated();
      if (!isValid) {
        localStorage.removeItem('accessToken');
        setAuth(false);
      }
      return isValid;
    };

    checkAuth();
  }, [location.pathname]);

  return (
    <Routes>
      <Route
        path="/"
        element={auth ? <Navigate to="/app/home" replace /> : <LoginPage setAuth={setAuth} />}
      />
      <Route
        path="/principal"
        element={(
          <PrivateRoute>
            <PrincipalPage setAuth={setAuth} />
          </PrivateRoute>
        )}
      />
      <Route
        path="/principal/:id"
        element={(
          <PrivateRoute>
            <ProgramPage />
          </PrivateRoute>
        )}
      />
      <Route
        path="/program/:id"
        element={(
          <PrivateRoute>
            <TenantPage />
          </PrivateRoute>
        )}
      />
      <Route
        path="/tenant/:id"
        element={(
          <PrivateRoute>
            <TenantDetailPage />
          </PrivateRoute>
        )}
      />
      <Route
        path="/tenant/edit/:id"
        element={(
          <PrivateRoute>
            <TenantEditPage />
          </PrivateRoute>
        )}
      />
      <Route
        path="/tenant/vpa/:id"
        element={(
          <PrivateRoute>
            <VpaPage />
          </PrivateRoute>
        )}
      />
      <Route
        path="/app"
        element={(
          <PrivateRoute>
            <AppLayout />
          </PrivateRoute>
        )}
      >
        <Route index element={<Navigate to="/app/home" replace />} />
        <Route path="home" element={<HomeScreen setAuth={setAuth} />} />
        <Route path="collect" element={<CollectScreen />} />
        <Route path="shops" element={<ShopsScreen />} />
        <Route path="shops/:tenantId" element={<ShopDetailScreen />} />
      </Route>
      <Route
        path="/tenant/vpa/add/:id"
        element={(
          <PrivateRoute>
            <QrScanner />
          </PrivateRoute>
        )}
      />
    </Routes>
  );
}

function App() {
  return (
    <div>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;

